import React, { useEffect, useMemo, useState } from "react";
import CommonFooter from "../../../core/common/footer/commonFooter";
import { DatePicker, Table, Spin } from "antd";
import TooltipIcons from "../../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../../core/common/tooltip-content/collapes";
import { all_routes } from "../../../Router/all_routes";
import { Link } from "react-router-dom";
import Select from "react-select";
import dayjs from "dayjs";
import {
  exportProductExpiryReportExcel,
  exportProductExpiryReportPdf,
  getProductExpiryReport,
  getProductExpiryReportFilters,
} from "../../../api/productExpiryReportApi";

const DEFAULT_PRODUCT_IMAGE = "assets/img/by-default.png";
const IMAGE_BASE = (
  process.env.REACT_APP_API_IMAGE_BASE_URL ||
  "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const isAbsoluteHttp = (url = "") => /^https?:\/\//i.test(url);
const isDataUrl = (url = "") => /^data:image/i.test(url);

const toProductImageUrl = (raw) => {
  if (!raw) return DEFAULT_PRODUCT_IMAGE;

  const baseVal = Array.isArray(raw) ? raw[0] : raw;
  const val =
    typeof baseVal === "object" && baseVal !== null
      ? baseVal.url ||
        baseVal.imageUrl ||
        baseVal.key ||
        baseVal.path ||
        baseVal.location ||
        baseVal.src ||
        ""
      : String(baseVal || "").trim();

  if (!val || typeof val !== "string") return DEFAULT_PRODUCT_IMAGE;

  const cleaned = val.replace(/^\/+(?=https?:\/\/)/i, "");
  if (isDataUrl(cleaned) || isAbsoluteHttp(cleaned)) return cleaned;

  const key = cleaned.replace(/^\/+/, "");
  if (!key) return DEFAULT_PRODUCT_IMAGE;

  return `${IMAGE_BASE}/${key}`;
};

const ProductExpiredreport = () => {
  const route = all_routes;
  const t = window.translate;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [filterOptions, setFilterOptions] = useState({
    stores: [],
    categories: [],
    brands: [],
    products: [],
  });

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const storeOptions = useMemo(
    () => [{ value: "All", label: t("All") }, ...(filterOptions.stores || [])],
    [filterOptions.stores, t],
  );

  const categoryOptions = useMemo(
    () => [{ value: "All", label: t("All") }, ...(filterOptions.categories || [])],
    [filterOptions.categories, t],
  );

  const brandOptions = useMemo(
    () => [{ value: "All", label: t("All") }, ...(filterOptions.brands || [])],
    [filterOptions.brands, t],
  );

  const productOptions = useMemo(
    () => [{ value: "All", label: t("All") }, ...(filterOptions.products || [])],
    [filterOptions.products, t],
  );

  const formatDate = (date) => {
    if (!date) return "-";
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return "-";
    return parsedDate.toLocaleDateString("en-GB");
  };

  const getReportParams = () => {
    const params = {};

    if (selectedDate) {
      params.date = dayjs(selectedDate).format("YYYY-MM-DD");
    }

    if (selectedStore?.value && selectedStore.value !== "All") {
      params.store = selectedStore.value;
    }

    if (selectedCategory?.value && selectedCategory.value !== "All") {
      params.category = selectedCategory.value;
    }

    if (selectedBrand?.value && selectedBrand.value !== "All") {
      params.brand = selectedBrand.value;
    }

    if (selectedProduct?.value && selectedProduct.value !== "All") {
      params.product = selectedProduct.value;
    }

    return params;
  };

  const fetchFilters = async () => {
    try {
      setFiltersLoading(true);
      const response = await getProductExpiryReportFilters();

      setFilterOptions({
        stores: response?.data?.stores || [],
        categories: response?.data?.categories || [],
        brands: response?.data?.brands || [],
        products: response?.data?.products || [],
      });
    } catch (error) {
      console.error(error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchReport = async (customParams = {}) => {
    try {
      setLoading(true);
      const response = await getProductExpiryReport(customParams);
      setData(response?.data || []);
    } catch (error) {
      console.error(error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchReport();
  }, []);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    const params = getReportParams();
    fetchReport(params);
  };

  const handleRefresh = () => {
    setSelectedDate(null);
    setSelectedStore(null);
    setSelectedCategory(null);
    setSelectedBrand(null);
    setSelectedProduct(null);
    fetchFilters();
    fetchReport();
  };

  const downloadBlobFile = (blobData, fileName) => {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const params = getReportParams();
      const blobData = await exportProductExpiryReportExcel(params);
      downloadBlobFile(blobData, "product-expiry-report.xlsx");
    } catch (error) {
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setExportLoading(true);
      const params = getReportParams();
      const blobData = await exportProductExpiryReportPdf(params);
      downloadBlobFile(blobData, "product-expiry-report.pdf");
    } catch (error) {
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: t("SKU"),
      dataIndex: "SKU",
      key: "SKU",
      render: (text) => (
        <span className="text-orange fw-medium">{text || "-"}</span>
      ),
      sorter: (a, b) => (a?.SKU || "").localeCompare(b?.SKU || ""),
      width: 140,
    },
    {
      title: t("Serial No"),
      dataIndex: "Serial_No",
      key: "Serial_No",
      sorter: (a, b) => (a?.Serial_No || "").localeCompare(b?.Serial_No || ""),
      width: 170,
    },
    {
      title: t("Product Name"),
      dataIndex: "Product_Name",
      key: "Product_Name",
      render: (text, record) => (
        <div className="d-flex align-items-center gap-2">
          <div className="avatar avatar-md flex-shrink-0">
            <img
              src={toProductImageUrl(
                record?.image || record?.productImage || record?.raw?.image,
              )}
              className="img-fluid"
              alt="product"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
              }}
            />
          </div>
          <div className="min-w-0">
            <p className="text-dark mb-0 fw-medium text-truncate">
              {text || "-"}
            </p>
          </div>
        </div>
      ),
      sorter: (a, b) =>
        (a?.Product_Name || "").localeCompare(b?.Product_Name || ""),
      width: 260,
    },
    {
      title: t("Store"),
      dataIndex: "store",
      key: "store",
      render: (text) => text || "-",
      sorter: (a, b) => (a?.store || "").localeCompare(b?.store || ""),
      width: 170,
    },
    {
      title: t("category"),
      dataIndex: "category",
      key: "category",
      render: (text) => text || "-",
      sorter: (a, b) => (a?.category || "").localeCompare(b?.category || ""),
      width: 170,
    },
    {
      title: t("brand"),
      dataIndex: "brand",
      key: "brand",
      render: (text) => text || "-",
      sorter: (a, b) => (a?.brand || "").localeCompare(b?.brand || ""),
      width: 170,
    },
    {
      title: t("Manufactured Date"),
      dataIndex: "Manufactured_Date",
      key: "Manufactured_Date",
      render: (date) => formatDate(date),
      sorter: (a, b) =>
        new Date(a?.Manufactured_Date || 0) -
        new Date(b?.Manufactured_Date || 0),
      width: 170,
    },
    {
      title: t("Expired Date"),
      dataIndex: "Expired_Date",
      key: "Expired_Date",
      render: (date) => formatDate(date),
      sorter: (a, b) =>
        new Date(a?.Expired_Date || 0) - new Date(b?.Expired_Date || 0),
      width: 170,
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="table-tab mb-4">
            <ul className="nav nav-pills flex-wrap row-gap-2">
              <li className="nav-item">
                <Link className="nav-link" to={route.productreport}>
                  {t("Product Report")}
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link active"
                  to={route.productexpiredreport}
                >
                  {t("Product Expiry Report")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={route.productquantityreport}>
                  {t("Product Quantity Alert")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="mb-1">{t("Product Expiry Report")}</h4>
                <h6 className="mb-0">{t("View Reports of Products")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={handleRefresh}
                  title={t("Refresh")}
                >
                  <RefreshIcon />
                </button>
              </li>
              <CollapesIcon />
            </ul>
          </div>

          <div className="card border-0 mb-4">
            <div className="card-body pb-1">
              <form onSubmit={handleGenerateReport}>
                <div className="row align-items-end g-3">
                  <div className="col-12 col-xl-10">
                    <div className="row g-3">
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="mb-0">
                          <label className="form-label">{t("Choose Date")}</label>
                          <div className="input-icon-start position-relative">
                            <DatePicker
                              className="form-control datetimepicker"
                              placeholder={t("dd/mm/yyyy")}
                              value={selectedDate}
                              onChange={(date) => setSelectedDate(date)}
                              style={{ width: "100%" }}
                              format="DD/MM/YYYY"
                            />
                            <span className="input-icon-left">
                              <i className="ti ti-calendar" />
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="mb-0">
                          <label className="form-label">{t("Store")}</label>
                          <Select
                            classNamePrefix="react-select"
                            options={storeOptions}
                            placeholder={
                              filtersLoading ? t("Loading...") : t("Choose")
                            }
                            value={selectedStore}
                            onChange={setSelectedStore}
                            isLoading={filtersLoading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-lg-2">
                        <div className="mb-0">
                          <label className="form-label">
                            {t("category")}
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            options={categoryOptions}
                            placeholder={
                              filtersLoading ? t("Loading...") : t("Choose")
                            }
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            isLoading={filtersLoading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-lg-2">
                        <div className="mb-0">
                          <label className="form-label">
                            {t("brand")}
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            options={brandOptions}
                            placeholder={
                              filtersLoading ? t("Loading...") : t("Choose")
                            }
                            value={selectedBrand}
                            onChange={setSelectedBrand}
                            isLoading={filtersLoading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-lg-2">
                        <div className="mb-0">
                          <label className="form-label">{t("Product")}</label>
                          <Select
                            classNamePrefix="react-select"
                            options={productOptions}
                            placeholder={
                              filtersLoading ? t("Loading...") : t("Choose")
                            }
                            value={selectedProduct}
                            onChange={setSelectedProduct}
                            isLoading={filtersLoading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-xl-2">
                    <div className="d-grid">
                      <button
                        className="btn btn-primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? t("Generating...") : t("Generate Report")}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card table-list-card no-search">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div>
                <h4 className="mb-0">{t("Product Expiry Report")}</h4>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <ul className="table-top-head mb-0">
                  <TooltipIcons />
                </ul>

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleExportExcel}
                  disabled={exportLoading}
                >
                  <i className="ti ti-file-spreadsheet me-1" />
                  {t("Excel")}
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleExportPdf}
                  disabled={exportLoading}
                >
                  <i className="ti ti-file-type-pdf me-1" />
                  {t("PDF")}
                </button>
              </div>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spin size="large" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={data}
                    rowKey={(record) =>
                      record?._id ||
                      `${record?.SKU || ""}-${record?.Serial_No || ""}-${record?.Product_Name || ""}`
                    }
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    scroll={{ x: 1500 }}
                    locale={{
                      emptyText: t("No expiry products found"),
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <CommonFooter />
      </div>
    </div>
  );
};

export default ProductExpiredreport;
